import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { V2AdminGetProfileChatByIdResponse } from '@workspace/api-backend'
import {
  v2AdminGetProfileChatByIdOptions,
  v2AdminGetProfileChatByIdQueryKey,
  v2AdminGetProfileChatTurnContextOptions,
  v2AdminRedactProfileChatTurnMutation,
} from '@workspace/api-backend/react-query'
import { Badge } from '@workspace/ui/components/badge'
import { ConfirmButton } from '@workspace/ui/components/confirm-button'
import { LabelValue } from '@workspace/ui/components/label-value'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { chatAttribution } from '../../../features/admin/lib/profile-chat-attribution'

/**
 * One conversation, every turn, with the question text as it was typed.
 *
 * Read-only apart from redaction: nothing here replays a turn against a new
 * prompt, and nothing here edits an answer. The corpus is evidence, and evidence
 * that can be edited from the page that reads it is not evidence.
 */

export const Route = createFileRoute('/admin/profile-chats/$conversationId')({
  component: Page,
})

// Generated from the backend's own schemas (AD-001): a renamed field is a type
// error here rather than a slide preview that silently stops appearing.
type Detail = V2AdminGetProfileChatByIdResponse['data']
type Turn = Detail['turns'][number]
type Bullet = Turn['bullets'][number]
type Source = Turn['sources'][number]
type Slide = Detail['slides'][string][number]

// Langfuse's own short trace route, which redirects to the trace inside
// whichever project owns it — the project id is not in the ledger and does not
// need to be.
const LANGFUSE_TRACE_URL = 'https://cloud.langfuse.com/trace'

function formatDateTime(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function statusVariant(status: string) {
  if (status === 'COMPLETE') return 'success' as const
  if (status === 'FAILED') return 'destructive' as const
  return 'secondary' as const
}

function Page() {
  const { conversationId } = Route.useParams()
  const queryClient = useQueryClient()

  const chatQuery = useQuery(v2AdminGetProfileChatByIdOptions({ path: { conversationId } }))

  const redactTurn = useMutation({
    ...v2AdminRedactProfileChatTurnMutation(),
    // Redaction also rewrites stored context snapshots, so any context already
    // fetched for this conversation is stale too — hence the prefix.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: v2AdminGetProfileChatByIdQueryKey({ path: { conversationId } }),
      }),
  })

  if (chatQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!chatQuery.data?.data) {
    return <div className="py-20 text-center text-muted-foreground">Not found</div>
  }

  const { conversation, turns } = chatQuery.data.data
  // Studio deploys on Vercel ahead of the backend's rollout, so for a few
  // minutes this page can be talking to a backend that does not send `slides`
  // yet. Missing must read as "no previews", never as a page that fails.
  const slidesByDeck = chatQuery.data.data.slides ?? {}

  return (
    <div className="w-full space-y-6">
      <div className="rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/admin/startups/$id"
            params={{ id: conversation.startupId }}
            className="text-base font-medium text-primary hover:underline"
          >
            {conversation.startupName}
          </Link>
          {conversation.anonymized && (
            <Badge variant="secondary" size="sm">
              Anonymised
            </Badge>
          )}
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <LabelValue label="Asked by" value={chatAttribution(conversation)} />
          <LabelValue label="Fund" value={conversation.investorName} />
          {/* Only beside a named contact. Without one, "Asked by" already IS
              the email, and a second row would repeat it. */}
          {conversation.contactName && (
            <LabelValue label="Visitor email" value={conversation.visitorEmail} />
          )}
          <LabelValue label="Link" value={conversation.linkName} />
          <LabelValue label="Started" value={formatDateTime(conversation.createdAt)} />
          <LabelValue label="Last question" value={formatDateTime(conversation.lastTurnAt)} />
        </div>
      </div>

      {groupIntoChats(turns).map((chat) => (
        <section key={chat.key} className="space-y-3">
          <header className="flex items-baseline justify-between gap-3 border-b border-border pb-1.5">
            <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {chat.number === null ? 'No chat id' : `Chat ${chat.number}`}
            </h2>
            <span className="text-xs text-muted-foreground">
              {chat.turns.length} {chat.turns.length === 1 ? 'question' : 'questions'} ·{' '}
              {formatDateTime(chat.turns[0]?.createdAt ?? '')}
            </span>
          </header>

          <ChatContextPanel conversationId={conversationId} turns={chat.turns} />

          {chat.turns.map((turn) => (
            <article key={turn.id} className="space-y-3 rounded-lg border border-border p-4">
              <header className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium tabular-nums">#{turn.ordinal}</span>
                <Badge variant="secondary" size="sm">
                  {turn.kind}
                </Badge>
                {turn.presetKey && (
                  <Badge variant="secondary" size="sm">
                    {turn.presetKey}
                  </Badge>
                )}
                {turn.slideNumber !== null && (
                  <Badge variant="secondary" size="sm">
                    slide {turn.slideNumber}
                  </Badge>
                )}
                {turn.topic && (
                  <Badge variant="secondary" size="sm">
                    {turn.topic}
                  </Badge>
                )}
                <Badge variant={statusVariant(turn.status)} size="sm">
                  {turn.status}
                </Badge>
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatDateTime(turn.createdAt)}
                </span>
              </header>

              <div className="space-y-1">
                <div className="flex items-start justify-between gap-3">
                  <p className="whitespace-pre-line text-sm font-medium">
                    {turn.questionText || <span className="text-muted-foreground">[redacted]</span>}
                  </p>
                  {turn.questionText && (
                    <ConfirmButton
                      className="h-8 w-20 shrink-0 text-xs"
                      confirmLabel="Sure?"
                      disabled={redactTurn.isPending}
                      onConfirm={() =>
                        redactTurn.mutate({ path: { conversationId, turnId: turn.id } })
                      }
                    >
                      Redact
                    </ConfirmButton>
                  )}
                </div>
                <p className="whitespace-pre-line text-sm text-muted-foreground">
                  {turn.answerText ?? '—'}
                </p>
              </div>

              <BulletLedger
                turnId={turn.id}
                bullets={turn.bullets ?? []}
                sources={turn.sources ?? []}
                slides={turn.deckDocumentId ? (slidesByDeck[turn.deckDocumentId] ?? []) : []}
              />

              {answerLevelViolations(turn).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {answerLevelViolations(turn).map((violation) => (
                    <Badge key={`${turn.id}-${violation}`} variant="destructive" size="sm">
                      {violation}
                    </Badge>
                  ))}
                </div>
              )}

              {turn.errorCode && (
                <p className="text-xs text-destructive">
                  {turn.errorCode}
                  {turn.errorMessage ? `: ${turn.errorMessage}` : ''}
                </p>
              )}

              <div className="grid gap-2 border-t border-border pt-3 text-xs md:grid-cols-4">
                <LabelValue label="Prompt" value={turn.promptVersion} />
                <LabelValue label="Model" value={turn.model} />
                <LabelValue label="Slides" value={turn.slideCount} />
                <LabelValue label="Deck" value={turn.deckDocumentId} />
                <LabelValue
                  label="Latency"
                  value={turn.latencyMs === null ? null : `${(turn.latencyMs / 1000).toFixed(1)}s`}
                />
                <LabelValue label="Record" value={turn.recordFingerprint} />
                <div>
                  <p className="text-muted-foreground">Trace</p>
                  {turn.langfuseTraceId ? (
                    <a
                      href={`${LANGFUSE_TRACE_URL}/${turn.langfuseTraceId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      Langfuse
                    </a>
                  ) : (
                    <p>—</p>
                  )}
                </div>
              </div>
            </article>
          ))}
        </section>
      ))}
    </div>
  )
}

/**
 * Violation kinds that are always about ONE bullet, never about the answer.
 *
 * Turns written before the ledger's `warnings` channel existed still carry these
 * in the flat list with nothing saying which bullet they belong to. Below the
 * table they would read as answer-level, which they are not — so once a turn
 * has a bullet table, they are not shown there at all.
 *
 * `lead_citation_without_date` is deliberately absent: the lead has no row, so
 * that one IS answer-level and must stay visible.
 */
const PER_BULLET_VIOLATIONS = [
  'citation_without_date',
  'bullet_is_one_sentence',
  'not_x_but_y',
  'filler_opener',
  'label_opener',
  'slide_does_not_support:',
]

function isPerBulletViolation(violation: string): boolean {
  return PER_BULLET_VIOLATIONS.some((kind) =>
    kind.endsWith(':') ? violation.startsWith(kind) : violation === kind
  )
}

/**
 * The violations the bullet table does NOT already show, deduplicated.
 *
 * `violations` is a flat append-only list, one entry per failing bullet, so a
 * turn where two bullets cite the same unsupported slide ends with
 * `slide_does_not_support:S8` twice — two identical red badges that say less
 * than the table's two ✕ marks, because they cannot say which bullets.
 *
 * So anything attributable to a bullet is dropped here and read off the table
 * instead. What survives is genuinely answer-level — `date_missing_from_answer`,
 * `lead_citation_without_date`, `too_many_bullets`, the lead-line rules — which
 * belongs to the turn and has nowhere else to be shown.
 */
function answerLevelViolations(turn: Turn): string[] {
  const bullets = turn.bullets ?? []
  const shownInTable = new Set<string>()
  for (const bullet of bullets) {
    for (const reason of bullet.reasons) shownInTable.add(reason)
    for (const warning of bullet.warnings) shownInTable.add(warning)
    // The engine's violation code is one string, `slide_does_not_support:ID`,
    // whichever of the two failing verdicts caused it — so the dedup key stays
    // that code regardless of which one this bullet actually got. The DISPLAY
    // label in `rowReasons` below is what tells CONTRADICTED from UNRELATED.
    for (const [sourceId, verdict] of Object.entries(bullet.attested)) {
      if (verdict !== 'supported') shownInTable.add(`slide_does_not_support:${sourceId}`)
    }
  }
  // A Set also collapses the duplicates that survive, so one gate firing on
  // three bullets is one badge, not three.
  const hasLedger = bullets.length > 0
  return [...new Set(turn.violations)].filter(
    (violation) => !shownInTable.has(violation) && !(hasLedger && isPerBulletViolation(violation))
  )
}

/**
 * The word for a failing verdict, on the row itself — not just on hover.
 * CONTRADICTED and UNRELATED are both bad, but they are different claims: one
 * says the cited slide disagrees, the other says it was never about this.
 */
function verdictReasonLabel(sourceId: string, verdict: Bullet['attested'][string]): string {
  return verdict === 'contradicted' ? `${sourceId} contradicts this` : `${sourceId} is unrelated`
}

/** The reasons to print on one row, drops first, then doubts, then warnings. */
function rowReasons(bullet: Bullet): { label: string; fatal: boolean }[] {
  const unsupported = Object.entries(bullet.attested)
    .filter(([, verdict]) => verdict !== 'supported')
    .map(([sourceId, verdict]) => verdictReasonLabel(sourceId, verdict))
  return [
    ...bullet.reasons.map((label) => ({ label, fatal: true })),
    // Not fatal today: re-attestation records its doubt but ships the bullet.
    // Red anyway, because a cited slide contradicting its claim is the most
    // serious thing on this row whether or not the gate enforced it.
    ...unsupported.map((label) => ({ label, fatal: true })),
    ...bullet.warnings.map((label) => ({ label, fatal: false })),
  ]
}

/**
 * Every bullet the model wrote for this turn, in the order it wrote them, with
 * what the pipeline did to each.
 *
 * The dropped rows are the reason this table exists. A turn's `violations` list
 * says a bullet was deleted and never which one, so a real grounding failure
 * and a gate misfiring on a true sentence look identical from the outside —
 * which is exactly how the attribution rule spent a week quietly deleting
 * correct sentences from real answers.
 *
 * Three distinctions the columns are careful about:
 *
 *  - **Dropped is not the same as retracted.** A dropped bullet never reached
 *    the reader; a retracted one was streamed and then taken off the screen
 *    when re-attestation contradicted a slide it cited.
 *  - **"not checked" is not "contradicted".** The attester times out, and the
 *    code treats a failed check as no evidence rather than as evidence against.
 *  - **A warning is not a drop.** The shape rules and an undated citation are
 *    reported and never enforced, so those bullets shipped; they sit beside
 *    `shown` in an outline badge rather than a red one.
 */
function BulletLedger({
  turnId,
  bullets,
  sources,
  slides,
}: {
  turnId: string
  bullets: Bullet[]
  sources: Source[]
  slides: Slide[]
}) {
  if (bullets.length === 0) return null

  const dropped = bullets.filter((bullet) => !bullet.kept).length
  // Counted separately from `dropped`: this page used to report a retracted
  // bullet as "shown", so it described an answer no reader ever saw.
  const retracted = bullets.filter((bullet) => bullet.kept && bullet.retracted).length

  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Bullets
        </p>
        <p className="text-xs text-muted-foreground">
          {bullets.length} written
          {dropped > 0 ? `, ${dropped} dropped before the reader saw them` : ', none dropped'}
          {retracted > 0 ? `, ${retracted} retracted after the reader saw them` : ''}
        </p>
      </div>

      {/* The text column can be long and the reasons column can be wide; the
          page body must never scroll sideways because of this table. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-xs">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="w-8 py-1.5 pr-2 font-medium">#</th>
              <th className="py-1.5 pr-3 font-medium">Bullet</th>
              <th className="w-40 py-1.5 pr-3 font-medium">Sources</th>
              <th className="w-56 py-1.5 font-medium">Resolution</th>
            </tr>
          </thead>
          <tbody>
            {bullets.map((bullet, index) => (
              <tr
                // Position IS the identity here: two bullets can carry the
                // same text, and this ledger is fixed once written — it is
                // never reordered, filtered or appended to.
                // biome-ignore lint/suspicious/noArrayIndexKey: see above
                key={`${turnId}-bullet-${index}`}
                className="border-b border-border/50 align-top last:border-b-0"
              >
                <td className="py-2 pr-2 text-muted-foreground tabular-nums">{index + 1}</td>
                <td
                  className={[
                    'py-2 pr-3 whitespace-pre-line',
                    bullet.kept && !bullet.retracted
                      ? ''
                      : 'text-muted-foreground line-through decoration-1',
                  ].join(' ')}
                >
                  {bullet.text}
                </td>
                <td className="py-2 pr-3">
                  {bullet.sourceIds.length === 0 ? (
                    <span className="text-muted-foreground">none</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {bullet.sourceIds.map((sourceId, sourceIndex) => (
                        <SourceBadge
                          // biome-ignore lint/suspicious/noArrayIndexKey: a bullet may cite the same source twice
                          key={`${turnId}-bullet-${index}-src-${sourceIndex}`}
                          sourceId={sourceId}
                          verdict={bullet.attested[sourceId]}
                          sources={sources}
                          slides={slides}
                        />
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-2">
                  {/* Every reason is written out here rather than left in a
                      tooltip on the source badge — the ✕ says THAT a source
                      disagreed, this says what the gate concluded. */}
                  <div className="flex flex-wrap gap-1">
                    <Badge
                      variant={bullet.kept && !bullet.retracted ? 'secondary' : 'destructive'}
                      size="sm"
                    >
                      {!bullet.kept ? 'dropped' : bullet.retracted ? 'retracted' : 'shown'}
                    </Badge>
                    {rowReasons(bullet).map((reason, reasonIndex) => (
                      <Badge
                        // biome-ignore lint/suspicious/noArrayIndexKey: a bullet may trip the same rule twice
                        key={`${turnId}-bullet-${index}-reason-${reasonIndex}`}
                        variant={reason.fatal ? 'destructive' : 'outline'}
                        size="sm"
                      >
                        {reason.label}
                      </Badge>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const SLIDE_ID_RE = /^S(\d+)$/

/**
 * What the re-attestation verdict means, in words. The mark alone cannot say
 * it: "never re-checked" (the attester timed out) and "checked and supported"
 * differ only by a missing ✓, and they are different claims.
 */
function verdictLabel(verdict: Bullet['attested'][string] | undefined): string {
  switch (verdict) {
    case undefined:
      return 'Not re-checked'
    case 'supported':
      return 'Re-checked: same topic, no conflict'
    case 'contradicted':
      return 'Re-checked: the slide states something different'
    case 'unrelated':
      return 'Re-checked: the slide is not about this'
  }
}

/**
 * A cited source you can check without leaving the page.
 *
 * Hovering `S8` shows slide 8 exactly as the model read it — the same rendered
 * PNG, from the deck this turn was answered from — under the verdict in words.
 * `L1` and `R` have no image, so they show the registry label the model was
 * given for them. Every badge has a hover, so the verdict is never unexplained.
 */
function SourceBadge({
  sourceId,
  verdict,
  sources,
  slides,
}: {
  sourceId: string
  verdict: Bullet['attested'][string] | undefined
  sources: Source[]
  slides: Slide[]
}) {
  const slidePage = Number(SLIDE_ID_RE.exec(sourceId)?.[1] ?? Number.NaN)
  const slide = slides.find((candidate) => candidate.page === slidePage)
  const label = sources.find((source) => source.id === sourceId)?.label
  // Only these two ever retracted the bullet this badge is on — a bare
  // "not supported" no longer exists, so there is nothing between them worth
  // a third colour.
  const failed = verdict === 'contradicted' || verdict === 'unrelated'

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={failed ? 'destructive' : 'secondary'} size="sm" className="cursor-help">
          {sourceId}
          {failed ? ' ✕' : verdict === 'supported' ? ' ✓' : ''}
        </Badge>
      </TooltipTrigger>
      <TooltipContent
        side="left"
        sideOffset={6}
        // The shared tooltip is a one-line text chip; a slide needs a card.
        className={
          slide
            ? 'max-w-none bg-background p-1.5 text-foreground shadow-lg ring-1 ring-border'
            : undefined
        }
      >
        {slide ? (
          <figure className="space-y-1">
            <img
              src={slide.url}
              alt={`Slide ${slide.page}`}
              className="block w-[420px] max-w-[60vw] rounded"
              loading="lazy"
            />
            <figcaption className="px-0.5 text-[11px] text-muted-foreground">
              Slide {slide.page} of {slides.length} · {verdictLabel(verdict)}
            </figcaption>
          </figure>
        ) : (
          <span>
            {label ? `${label} · ` : ''}
            {verdictLabel(verdict)}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * Everything the model received, for one chat, collapsed until asked for.
 *
 * Per chat and per turn, never "the conversation's latest": history is scoped
 * to one chat and capped, so the last turn's context does not contain the
 * earlier chats' — showing it for them would put in front of an admin context
 * those turns never had. Any turn of the chat can be picked; the latest is the
 * default because it carries the most of that chat's history.
 *
 * The text is fetched only when opened: each snapshot is a full copy of the
 * system prompt, and shipping all of them with the page cost tens of KB per turn
 * on every load.
 */
function ChatContextPanel({ conversationId, turns }: { conversationId: string; turns: Turn[] }) {
  const withContext = turns.filter((turn) => turn.hasContextSnapshot)
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = withContext.find((turn) => turn.id === selectedId) ?? withContext.at(-1)

  const contextQuery = useQuery({
    ...v2AdminGetProfileChatTurnContextOptions({
      path: { conversationId, turnId: selected?.id ?? '' },
    }),
    enabled: open && selected !== undefined,
  })

  if (!selected) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-3 text-xs text-muted-foreground">
        No context recorded for this chat — its turns predate the snapshot.
      </p>
    )
  }

  // The profile record and the deck are the two inputs a single snapshot can
  // silently misrepresent for the other turns of the same chat.
  const recordChanged =
    new Set(turns.map((turn) => turn.recordFingerprint).filter(Boolean)).size > 1
  const deckChanged = new Set(turns.map((turn) => turn.deckDocumentId).filter(Boolean)).size > 1
  const snapshot = contextQuery.data?.data.contextSnapshot ?? null

  return (
    <details
      className="group rounded-lg border border-border"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm">
        <span className="font-medium">Context passed to the model</span>
        <span className="text-xs text-muted-foreground">
          as sent for turn #{selected.ordinal}
          <span className="ml-2 inline-block transition-transform group-open:rotate-90">›</span>
        </span>
      </summary>
      <div className="space-y-2 border-t border-border px-4 py-3">
        {withContext.length > 1 && (
          <Select value={selected.id} onValueChange={setSelectedId}>
            <SelectTrigger className="h-8 w-64 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {withContext.map((turn) => (
                <SelectItem key={turn.id} value={turn.id} className="text-xs">
                  Turn #{turn.ordinal} · {turn.questionText || '[redacted]'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {(recordChanged || deckChanged) && (
          <p className="text-xs text-destructive">
            The{' '}
            {recordChanged && deckChanged
              ? 'profile record and deck'
              : recordChanged
                ? 'profile record'
                : 'deck'}{' '}
            changed during this chat, so other turns were answered from different context than the
            one shown here.
          </p>
        )}
        {contextQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          // Wide lines scroll inside the box; the page itself never scrolls
          // sideways because of a long prompt line.
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded bg-muted/40 p-3 font-mono text-[11.5px] leading-relaxed">
            {snapshot ?? 'This turn stored no context.'}
          </pre>
        )}
      </div>
    </details>
  )
}

/** `number` counts real chats only, so a group without an id never takes "Chat 1". */
type Chat = { key: string; sessionId: string | null; number: number | null; turns: Turn[] }

/**
 * The turns as the reader experienced them: one group per chat.
 *
 * A conversation is every question one visitor ever asked in this room, across
 * reloads and "Clear chat". The share page never shows that — it shows one chat
 * at a time — so reading it as one long thread misrepresents what the investor
 * saw, and hides that a follow-up was asked into an empty pane.
 *
 * Collected BY ID, ordered by each chat's first question. Grouping runs of
 * consecutive turns instead split one chat in two whenever a turn without an id
 * landed between its questions — which is exactly what a tab opened before
 * chats were recorded, still open beside a new one, produces. Turns without an
 * id are kept in one group of their own rather than guessed into chats by time
 * gaps, and it is labelled by what it is, not by when it happened: it need not
 * come first.
 */
function groupIntoChats(turns: Turn[]): Chat[] {
  const byId = new Map<string, Chat>()
  let chatsSeen = 0
  for (const turn of turns) {
    const id = turn.chatSessionId ?? ''
    let chat = byId.get(id)
    if (!chat) {
      chat = {
        key: turn.id,
        sessionId: turn.chatSessionId,
        number: turn.chatSessionId ? ++chatsSeen : null,
        turns: [],
      }
      byId.set(id, chat)
    }
    chat.turns.push(turn)
  }
  return [...byId.values()]
}
